import { useNavigate } from "react-router-dom";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPHKLCandidateProfileAsync /* PHKL Custom */,
  reset,
} from "../../../auth/redux/auth-slice";
import { selectCandidateProfile, selectToken } from "../../../auth/redux/selector";
import { TokenPayload } from "../../../auth/network/type";
import {
  CORE_URL,
  fakeToken,
  getLoginUserData,
  getToken,
  languageSelectionConfig,
  parseJwt,
  DOCEBO, // PHKL Custom
} from "../../utils";
import { get, isEmpty, merge } from "lodash";
import { confirm } from "../../../../components/modal/comfirm";
import { tipsPopup } from "../../../../components/modal/tips";
import { checkboxPopup } from "../../../../components/modal/checkbox/checkbox.component";
import { CandidateStatus } from "../../types";
import { color } from "../../../../mui-theme/palette";
import { getMessageUnreadCount, postLogin } from "../../network/network";
import { selectOnboardingTrainingSummary } from "../../redux/selector";
import store from "../../../../redux/store";
import { PageNameEnum } from "../menu/type";
import { fetchConfigAsync, getOnboardingTrainingSummaryAsync } from "../../redux/onboarding-slice";
import { getItem, LOCAL_STORAGE_KEY, setItem } from "../../../../utils/local-storage";
import { TakeUIClickEventParameters } from "../../../../common/ga/types/ga-general-type";
import { takeUIClickEvent } from "../../../../common/ga/ga";
import { ApplicationEnum } from "../../../../constants/constants"; // Add ApplicationEnum
import { getDeviceAndBrowserType, useA2HS } from "../../../../utils/hooks/use-a2hs";
import { isStandalone } from "../../../../utils/standalone";
import { registerDeviceForPushNotification } from "../../../../utils/notification-utils";
import {
  registerPushNotification,
  unregisterNotificationDevice,
} from "../../../notification/redux/notification-slice";
import { SESSION_STORAGE_KEY } from "../../../../utils/session-storage";
import { isBrowserSupported } from "../../../../firebase-config";
import licenseExamLogo from "../../../../components/icon/assets/license-exam.svg"; // PHKL Custom
import mockExamLogo from "../../../../components/icon/assets/mock-exam.svg"; // PHKL Custom
// Add mock exam logo
import { PruToast } from "../../../../components/pru-toast";
import { OnboardingTrainingPlatform } from "../course-menu-screen/type";
import { useOnboardingTrainingCommon } from "./hooks/use-onboarding-training-common";
import studyMaterialsIcon from "../../../../components/icon/assets/study-materials.svg"; // PHKL Custom
import mockExamIcon from "../../../../components/icon/assets/mock-exam-m.svg"; // Add mock exam logo
import courcePracticeIcon from "../../../../components/icon/assets/cource-practice.svg"; // PHKL Custom
import classRegistrationIcon from "../../../../components/icon/assets/class-registration.svg"; // PHKL Custom
import { takePHKLGAUIClickEvent } from "../../ga/ga"; // PHKL Custom
import { PHKLTakeUIClickEventParameters } from "../../ga/types/phkl-ga-general-type";
import {
  ONBOARDING_TRAINING_OBJECT_NAME,
  PHKCandidateLanding,
  PHKCandidateLandingScreenName,
  PHKLCandidateGAFeature,
  PHKLCandidateGAModule,
  PHKLOBJECT_TYPE,
} from "../../ga/types/phkl-ga-parameter-type";

export const useCandidateHome = () => {
  const navigate = useNavigate();
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [isSettingPopupOpen, setIsSettingPopupOpen] = useState(false);
  const [candidateId, setCandidateId] = useState("");
  const [configLoading, setConfigLoading] = useState(false); // PHKL Custom
  const [summaryLoading, setSummaryLoading] = useState(false); // PHKL Custom
  const recruitmentConfig = useSelector((state: any) => state.onboarding.configs.Recruitment);
  const enableStandardSSO = get(recruitmentConfig, "enableStandardSSO", false);
  const enableReportPRUDNA = get(recruitmentConfig, "enableReportPRUDNA", false);
  const { onboardingTrainingIsActive } = useOnboardingTrainingCommon();

  const tokenData = useMemo(() => {
    const tokenFromStorage = getToken();
    const tokenPayload = parseJwt(tokenFromStorage);
    return merge(tokenPayload, token);
  }, [token]);

  const candidateProfile = useSelector(selectCandidateProfile);
  const candidateOnboardingTrainingSummary = useSelector(selectOnboardingTrainingSummary);
  const { triggerA2HSPrompt } = useA2HS();

  const { device } = getDeviceAndBrowserType();
  const isInPWAMode = isStandalone();
  const [showEnableNotificationBanners, setShowEnableNotificationBanners] = useState(
    () =>
      !isInPWAMode &&
      device === "ios" &&
      localStorage.getItem(LOCAL_STORAGE_KEY.enable_notifications_banner_display) !== "1",
  );

  const closeEnableNotificationBanners = () => {
    setShowEnableNotificationBanners(false);
    localStorage.setItem(LOCAL_STORAGE_KEY.enable_notifications_banner_display, "1");
  };

  const handleEnableShortcut = () => {
    triggerA2HSPrompt(true); // Force trigger A2HS prompt
    const gaData: TakeUIClickEventParameters = {
      module: "Settings",
      feature: "A2HS",
      journey: "add_to_homescreen",
      stage: "trigger_prompt",
      screen_id: "SCR_STTNG",
      screen_name: "Settings-Screen",
      object_name: "Enable_Shortcut",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const requestPushNotification = useCallback(async () => {
    if (!isBrowserSupported()) {
      console.warn("Push notifications are not supported in this browser");
      return;
    }

    const user = getLoginUserData();
    const userTokenIssuedAt = user?.iat;
    if (userTokenIssuedAt) {
      // declare a flag to avoid re-registering the same token
      const localStorageKey = `isTokenRegistered_${userTokenIssuedAt}`;
      const isTokenRegistered = localStorage.getItem(localStorageKey);
      if (isTokenRegistered === "true") {
        // Token already registered
        const remindMeLaterTsString = localStorage.getItem(
          LOCAL_STORAGE_KEY.REMIND_ME_LATER_NOTIFICATION_TS,
        );
        if (remindMeLaterTsString) {
          const reminderMeLaterTs = parseInt(remindMeLaterTsString);
          if (reminderMeLaterTs > Date.now()) {
            // not yet time to remind
            return;
          }
        }
      }
      localStorage.setItem(localStorageKey, "true"); // Mark token as registered
    }

    const permission = Notification.permission;

    // Only trigger permission request if permission is 'default'
    if (permission === "default") {
      try {
        await registerDeviceForPushNotification({
          dispatch,
        });
      } catch (error) {
        console.error("Failed to request push notification permission:", error);
      }
    } else if (permission === "granted") {
      const userToggleWebPush = localStorage.getItem(LOCAL_STORAGE_KEY.user_toggle_web_push);
      if (userToggleWebPush !== "off") {
        dispatch<any>(registerPushNotification());
      }
    }
  }, [dispatch]);

  useEffect(() => {
    postLogin(); // PHKL Custom
    setConfigLoading(true); // PHKL Custom
    dispatch<any>(fetchConfigAsync())
      .then(requestPushNotification)
      .finally(() => {
        setConfigLoading(false); // PHKL Custom
      });
  }, []);
  // PHKL Custom -s
  useEffect(() => {
    let loginUser = token;
    if (getToken() !== fakeToken) {
      if (!loginUser && isStandalone()) {
        loginUser = getLoginUserData();
      }
      if (loginUser) {
        if (!isInPWAMode) {
          triggerA2HSPrompt();
        }
      }
    }
  }, [token]);
  // PHKL Custom -e

  const { enableOnboardingTraining, enableOnboardingExamRegistry, enableMockExam } = useMemo(() => {
    const { licenseExamVisibleOn, onboardingTrainingVisibleOn, mockExam } = get(
      store.getState(),
      "onboarding.configs.Recruitment",
      {},
    );
    return {
      enableOnboardingTraining:
        !isEmpty(candidateOnboardingTrainingSummary?.trainingSummary) &&
        candidateOnboardingTrainingSummary?.trainingSummary.active &&
        candidateOnboardingTrainingSummary?.trainingSummary.requiredToAttend !== false &&
        onboardingTrainingVisibleOn?.includes?.(PageNameEnum.OnboardingApplication),
      enableOnboardingExamRegistry:
        !isEmpty(candidateOnboardingTrainingSummary?.examSummary) &&
        candidateOnboardingTrainingSummary?.examSummary.requiredToAttend &&
        licenseExamVisibleOn?.includes?.(PageNameEnum.OnboardingApplication),
      enableMockExam: mockExam?.enabled || false,
    };
  }, [candidateOnboardingTrainingSummary]);

  useEffect(() => {
    getMessageUnreadCount().then((res) => {
      const unreadCount = res?.data?.data;
      setInboxUnreadCount((pre) => unreadCount ?? pre);
    });
  }, []);

  useEffect(() => {
    // get training & license summary
    if (candidateId) {
      setSummaryLoading(true); // PHKL Custom
      dispatch<any>(
        getOnboardingTrainingSummaryAsync({ candidateId: candidateId as string }),
      ).finally(() => {
        setSummaryLoading(false); // PHKL Custom
      });
    }
  }, [candidateId, dispatch]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (tokenData) {
          const res: TokenPayload = tokenData;
          const candidateId = res?.sub;
          if (candidateId) {
            setCandidateId(candidateId);
            await dispatch<any>(getPHKLCandidateProfileAsync()); //PHKL Custom
          }
        }
      } catch (error) {
        console.error("Failed to fetch candidate profile:", error);
      }
    };

    fetchProfile();
  }, [dispatch, tokenData]);

  // PHKL Custom -s
  useEffect(() => {
    if (candidateProfile && !candidateProfile?.isNamesSet) {
      navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/edit-info`, {
        state: { candidateProfile, isNew: true },
      });
    }
  }, [candidateProfile]);
  // PHKL Custom -e

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // PHKL Custom -s
  const MENU_TIEMS = [
    {
      id: "study-materials",
      label: "Recruitment.study_materials",
      icon: studyMaterialsIcon,
      onClick: () => {
        redirectToDoceboPage(
          DOCEBO.STUDY_MATERIALS,
          ONBOARDING_TRAINING_OBJECT_NAME.STUDY_MATERIALS,
        );
      },
    },
    {
      id: "course-enrolment",
      label: "Recruitment.course_enrolment",
      icon: classRegistrationIcon,
      onClick: () => {
        redirectToDoceboPage(
          DOCEBO.COURSE_ENROLMENT,
          ONBOARDING_TRAINING_OBJECT_NAME.COURSE_ENROLMENT,
        );
      },
    },
    {
      id: "mock-exam",
      label: "Recruitment.mock_exam",
      icon: mockExamIcon,
      onClick: () => {
        redirectToDoceboPage(DOCEBO.MOCK_EXAM, ONBOARDING_TRAINING_OBJECT_NAME.MOCK_EXAM);
      },
    },
    {
      id: "practice-questions",
      label: "Recruitment.practice_questions",
      icon: courcePracticeIcon,
      onClick: () => {
        redirectToDoceboPage(
          DOCEBO.PRACTICE_QUESTIONS,
          ONBOARDING_TRAINING_OBJECT_NAME.PRACTICE_QUESTIONS,
        );
      },
    },
  ];
  // PHKL Custom -e
  const clickInbox = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/inbox`);
  };

  const handleLanguageClick = () => {
    checkboxPopup({
      title: t("Common.language"),
      selectData: { value: getItem("i18nextLng") as string },
      data: languageSelectionConfig().availableLanguages.map((lang) => ({
        label: lang.languageFullName,
        value: lang.i18nLanguageCode,
      })),
      onChange: (data) => {
        if (data.value) {
          setItem("i18nextLng", data.value);
          i18n.changeLanguage(data.value);
        }
      },
    });
    setIsSettingPopupOpen(false);
  };
  /* PHKL Custom -s
  const handleGuideClick = () => {
    const recruitmentConfig = get(store.getState(), "onboarding.configs.Recruitment", {});

    const { PUBLIC_URL } = process.env;
    const defaultGuideUrl = PUBLIC_URL
      ? `${PUBLIC_URL}/static/candidate_guide.pdf`
      : "/static/candidate_guide.pdf";
    const guideUrl = recruitmentConfig.candidateGuideUrl || defaultGuideUrl;
    window.open(guideUrl, "_blank");
  };
  PHKL Custom -e */

  const handleDisclaimerClick = () => {
    tipsPopup({ title: t("Common.disclaimer"), message: t("Settings.disclaimer_content") });
    setIsSettingPopupOpen(false);
  };

  const handlePrivacyPolicyClick = () => {
    tipsPopup({
      title: t("Common.privacy_notice"),
      message: t("Settings.privacy_policy_content"),
    });
    setIsSettingPopupOpen(false);
  };

  const clickLogout = () => {
    confirm({
      title: t("Common.are_you_sure_you_want_to_log_out"),
      message: "",
      confirmButtonText: t("global.text.confirm"),
      cancelButtonText: t("Cancel"),
    }).then((result) => {
      if (result.ok) {
        dispatch<any>(unregisterNotificationDevice());
        // TODO: call api to logout
        sessionStorage.removeItem(SESSION_STORAGE_KEY.AUTH_TOKEN);
        dispatch(reset());
        navigate(`${ROOT_PATH}/login`);
      }
    });
  };

  const clickSetting = () => {
    if (isPortrait) {
      navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/setting`);
    } else {
      setIsSettingPopupOpen(!isSettingPopupOpen);
    }
  };

  const clickPopup = () => {
    setIsSettingPopupOpen(!isSettingPopupOpen);
  };

  const clickProfile = useCallback(() => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/edit-info` /* PHKL Custom */, {
      state: {
        candidateProfile,
      },
    });

    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "CandidateProfile",
      journey: "view_profile",
      stage: "view_profile",
      screen_id: "SCR_RCRTMT_CNDDT_PRFL",
      screen_name: "Recruitment-Candidate-Profile-Screen",
      object_name: "View",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  }, [navigate, candidateProfile]);
  /* PHKL Custom -s
  const clickPrudna = () => {};
  const clickTraining = () => {
    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "Training",
      journey: "feature_entry",
      stage: "feature_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "Training",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };
  PHKL Custom -e  */

  const clickLicenseExam = () => {
    // PHKL Custom -s
    const PhHKLGaData: PHKLTakeUIClickEventParameters = {
      module: PHKLCandidateGAModule,
      feature: PHKLCandidateGAFeature,
      journey: PHKCandidateLanding,
      stage: "view_menu",
      screen_name: PHKCandidateLandingScreenName,
      object_name: ONBOARDING_TRAINING_OBJECT_NAME.EXAM_REGISTRATION,
      object_type: PHKLOBJECT_TYPE.BUTTON,
      item_value: candidateProfile?.candidateCode,
      item_value_2: candidateProfile?.agentid,
    };
    takePHKLGAUIClickEvent(PhHKLGaData);
    // PHKL Custom -e
    navigate(
      `${ROOT_PATH}/${ONBOARDING_PATH}/exams?candidateId=${candidateId}&lang=${i18n.language}`,
    );
    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "License",
      journey: "view_license_menu",
      stage: "journey_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "License",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const clickMockExam = async () => {
    const application = ApplicationEnum.DOCEBO;

    // const redirectUrl = encodeURI(
    //   `learn?lang=${DoceboLanguageMapping[i18n.language] ?? "en"}`,
    // );

    // PHKL Custom -s
    // const redirectUrl = encodeURI(`learn`);
    // window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&redirectTo=/learn&subscription-key=${get(
    //   window,
    //   "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
    //   "",
    // )}&application=${application}&grantType=saml&idp=saam`;

    const redirectUrl = encodeURI(DOCEBO.MOCK_EXAM);
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;
    // PHKL Custom -e

    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "MockExam",
      journey: "view_mock_exam_menu",
      stage: "journey_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "MockExam",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const clickOnboardingTraining = async () => {
    const trainingPlatform = get(
      store.getState(),
      "onboarding.configs.Recruitment.trainingPlatform",
      OnboardingTrainingPlatform.COE,
    );
    // if onboarding training is not active, show alert and do not proceed
    if (!onboardingTrainingIsActive) {
      PruToast({ message: t("Recruitment.training_content_unavailable") });
      return;
    }
    // PHKL Custom -s
    // if (trainingPlatform === OnboardingTrainingPlatform.COE) {
    //   clickTraining();
    //   return;
    // }
    const PhHKLGaData: PHKLTakeUIClickEventParameters = {
      module: PHKLCandidateGAModule,
      feature: PHKLCandidateGAFeature,
      journey: PHKCandidateLanding,
      stage: "view_menu",
      screen_name: PHKCandidateLandingScreenName,
      object_name: ONBOARDING_TRAINING_OBJECT_NAME.ONBOARDING_TRAINING,
      object_type: PHKLOBJECT_TYPE.BUTTON,
      item_value: candidateProfile?.candidateCode,
      item_value_2: candidateProfile?.agentid,
    };
    takePHKLGAUIClickEvent(PhHKLGaData);
    // PHKL Custom -e

    const application = ApplicationEnum.DOCEBO;
    const redirectUrl = encodeURI(DOCEBO.ONBOARDING_TRAINING); // PHKL Custom
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;

    const gaData: TakeUIClickEventParameters = {
      module: "Recruitment",
      feature: "OnboardingTraining",
      journey: "feature_entry",
      stage: "journey_entry",
      screen_id: "SCR_RCRTMT_HM",
      screen_name: "Recruitment-Home-Screen",
      object_name: "OnboardingTraining",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };
  /* PHKL Custom -s*/
  const redirectToDoceboPage = (url: string, id: ONBOARDING_TRAINING_OBJECT_NAME) => {
    const gaData: PHKLTakeUIClickEventParameters = {
      module: PHKLCandidateGAModule,
      feature: PHKLCandidateGAFeature,
      journey: PHKCandidateLanding,
      stage: "view_menu",
      screen_name: PHKCandidateLandingScreenName,
      object_name: id,
      object_type: PHKLOBJECT_TYPE.BUTTON,
      item_value: candidateProfile?.candidateCode,
      item_value_2: candidateProfile?.agentid,
    };
    takePHKLGAUIClickEvent(gaData);

    const application = ApplicationEnum.DOCEBO;
    const redirectUrl = encodeURI(url);
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;
  };
  /* PHKL Custom -e*/

  const labelOption = (
    status?: CandidateStatus,
  ): { backgroundColor: string; color: string; text: string } => {
    const _labelOption: { backgroundColor: string; color: string; text: string } = {
      backgroundColor: "",
      color: "",
      text: "",
    };

    switch (status) {
      case CandidateStatus.REJECTED:
        _labelOption.backgroundColor = color.orangeLight;
        _labelOption.color = color.orange;
        _labelOption.text = t("Common.rejected");
        break;
      case CandidateStatus.ACCEPTED:
        _labelOption.backgroundColor = color.greenLight;
        _labelOption.color = color.green;
        _labelOption.text = t("Common.accepted");
        break;
      case CandidateStatus.EXPIRED:
        _labelOption.backgroundColor = color.redLight;
        _labelOption.color = color.majorRed;
        _labelOption.text = t("Recruitment.expired");
        break;
      case CandidateStatus.CANCELLED:
        _labelOption.backgroundColor = color.greyBg;
        _labelOption.color = color.grey33;
        _labelOption.text = t("Recruitment.candidate_cancelled");
        break;
      default:
        _labelOption.backgroundColor = color.blueLight;
        _labelOption.color = color.blue;
        _labelOption.text = t("Recruitment.in_progress");
    }
    return _labelOption;
  };

  return {
    t,
    clickInbox,
    clickSetting,
    clickProfile,
    clickLogout,
    // handleGuideClick,//PHKL Custom
    handleDisclaimerClick,
    handlePrivacyPolicyClick,
    handleLanguageClick,
    clickPopup,
    isPortrait,
    candidateProfile,
    isSettingPopupOpen,
    labelOption,
    inboxUnreadCount,
    clickLicenseExam,
    // clickPrudna,//PHKL Custom
    // clickTraining,//PHKL Custom
    clickMockExam,
    clickOnboardingTraining,
    enableOnboardingExamRegistry,
    enableMockExam,
    enableOnboardingTraining,
    handleEnableShortcut,
    showEnableNotificationBanners,
    closeEnableNotificationBanners,
    loading: configLoading || summaryLoading, // PHKL Custom
    MENU_TIEMS, // PHKL Custom
    enableStandardSSO,
    enableReportPRUDNA,
  };
};
